/**
 * 内容仓库 —— Swift ContentRepository.swift 的 TypeScript 镜像。
 * 负责加载、解码、校验 encyclopedia.json，并提供按 id/topic 的查询。
 */

import type { ContentPackage, EncyclopediaEntry, EntryKind } from './models';
import { decodeContentPackage } from './models';
import { topic as topicByID } from './moduleCatalog';

export class ContentValidationError extends Error {
  constructor(
    message: string,
    readonly issues: string[],
  ) {
    super(message);
    this.name = 'ContentValidationError';
  }
}

export interface ContentRepository {
  readonly package: ContentPackage;
  readonly entries: readonly EncyclopediaEntry[];
  entry(id: string): EncyclopediaEntry | undefined;
  entriesForTopic(topicID: string): EncyclopediaEntry[];
  entriesForModule(moduleID: string): EncyclopediaEntry[];
  entriesForKind(kind: EntryKind): EncyclopediaEntry[];
  entriesInIDs(ids: readonly string[]): EncyclopediaEntry[];
}

/**
 * 校验解码后的内容包，镜像 Swift ContentRepository 的校验规则：
 * - schemaVersion 必须为 2。
 * - 条目 id 必须非空且唯一。
 * - 必填字段（nameZh/nameEn/topicID）不可缺失。
 */
export function validatePackage(pkg: ContentPackage): void {
  const issues: string[] = [];

  if (pkg.schemaVersion !== 2) {
    issues.push(
      `schemaVersion 应为 2，实际为 ${pkg.schemaVersion}（内容包与程序版本不匹配）。`,
    );
  }

  const seenIDs = new Set<string>();
  for (const entry of pkg.entries) {
    const prefix = `条目 [${entry.id || '(空 id)'}]`;
    if (!entry.id) {
      issues.push(`${prefix} id 为空。`);
      continue;
    }
    if (seenIDs.has(entry.id)) {
      issues.push(`${prefix} id 重复。`);
    }
    seenIDs.add(entry.id);

    if (!entry.nameZh) issues.push(`${prefix} nameZh 为空。`);
    if (!entry.nameEn) issues.push(`${prefix} nameEn 为空。`);
    if (!entry.topicID) issues.push(`${prefix} topicID 为空。`);
  }

  if (issues.length > 0) {
    throw new ContentValidationError(
      `内容包校验失败，共 ${issues.length} 项问题。`,
      issues,
    );
  }
}

export function createRepository(pkg: ContentPackage): ContentRepository {
  const byID = new Map(pkg.entries.map((e) => [e.id, e]));

  return {
    package: pkg,
    entries: pkg.entries,
    entry(id) {
      return byID.get(id);
    },
    entriesForTopic(topicID) {
      return pkg.entries.filter((e) => e.topicID === topicID);
    },
    entriesForModule(moduleID) {
      return pkg.entries.filter((e) => topicByID(e.topicID)?.moduleID === moduleID);
    },
    entriesForKind(kind) {
      return pkg.entries.filter((e) => e.kind === kind);
    },
    entriesInIDs(ids) {
      const result: EncyclopediaEntry[] = [];
      for (const id of ids) {
        const e = byID.get(id);
        if (e) result.push(e);
      }
      return result;
    },
  };
}

/**
 * 从原始 JSON 构建仓库：解码 + 校验。
 * 镜像 Swift ContentRepository.load()。
 */
export function loadRepository(raw: unknown): ContentRepository {
  const pkg = decodeContentPackage(raw as Parameters<typeof decodeContentPackage>[0]);
  validatePackage(pkg);
  return createRepository(pkg);
}
